import { createContext, useContext, ReactNode, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import authClient from '../auth/authClient';

interface Organization {
  id: string;
  name: string;
  slug: string;
  logo?: string | null;
}

interface OrgContextValue {
  organizations: Organization[];
  activeOrg: Organization | null;
  isLoading: boolean;
  orgSlug: string | null;
  setActiveOrg: (slug: string) => Promise<void>;
}

const OrgContext = createContext<OrgContextValue | null>(null);

interface OrgProviderProps {
  children: ReactNode;
}

export function OrgProvider({ children }: OrgProviderProps) {
  const { data: organizations, isPending: orgsLoading } = authClient.useListOrganizations();
  const { data: activeOrganization, isPending: activeLoading } = authClient.useActiveOrganization();
  const navigate = useNavigate();
  const location = useLocation();

  const isLoading = orgsLoading || activeLoading;

  const setActiveOrg = useCallback(async (slug: string) => {
    await authClient.organization.setActive({ organizationSlug: slug });

    // Get current path without org prefix
    const pathParts = location.pathname.split('/').filter(Boolean);
    const currentOrgSlug = pathParts[0];
    const isOrgRoute = organizations?.some(org => org.slug === currentOrgSlug);

    // Navigate to new org with same sub-path
    if (isOrgRoute && pathParts.length > 1) {
      const subPath = pathParts.slice(1).join('/');
      navigate(`/${slug}/${subPath}`);
    } else {
      navigate(`/${slug}`);
    }
  }, [location.pathname, organizations, navigate]);

  const value = useMemo<OrgContextValue>(() => ({
    organizations: (organizations || []) as Organization[],
    activeOrg: activeOrganization as Organization | null,
    isLoading,
    orgSlug: activeOrganization?.slug || null,
    setActiveOrg,
  }), [organizations, activeOrganization, isLoading, setActiveOrg]);

  return (
    <OrgContext.Provider value={value}>
      {children}
    </OrgContext.Provider>
  );
}

export function useOrg() {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error('useOrg must be used within an OrgProvider');
  }
  return context;
}

export function useOrgNavigate() {
  const { orgSlug } = useOrg();
  const navigate = useNavigate();

  return useCallback((path: string) => {
    if (path.startsWith('/')) {
      // Absolute path - prefix with org
      navigate(`/${orgSlug}${path}`);
    } else {
      // Relative path
      navigate(path);
    }
  }, [orgSlug, navigate]);
}
